import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Badge, Row, Col, Card, Form, Spinner, Tabs, Tab } from 'react-bootstrap';
import { FaHistory, FaDownload, FaFilter, FaSearch, FaEye, FaMoneyBillWave, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import api from '../services/api';
import toast from 'react-hot-toast';

const WalletStatementModal = ({ show, onHide }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [summary, setSummary] = useState({});
  const [currentBalance, setCurrentBalance] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    type: 'all',
    date_from: '',
    date_to: '',
    search: ''
  });
  
  const [activeTab, setActiveTab] = useState('statement');

  // Fetch wallet statement
  const fetchStatement = async (page = 1, reset = false) => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        type: filters.type,
        ...(filters.date_from && { date_from: filters.date_from }),
        ...(filters.date_to && { date_to: filters.date_to })
      });
      
      const response = await api.get(`/api/wallet/statement?${params}`);
      
      if (response.data.success) {
        const data = response.data.data;
        
        if (reset) {
          setTransactions(data.transactions);
        } else {
          setTransactions(prev => [...prev, ...data.transactions]);
        }
        
        setCurrentPage(data.pagination.current_page);
        setTotalPages(data.pagination.last_page);
        setHasMore(data.pagination.has_more);
        setSummary(data.summary);
        setCurrentBalance(data.current_balance);
      }
    } catch (error) {
      console.error('Failed to fetch statement:', error);
      toast.error('Failed to load wallet statement');
    } finally {
      setLoading(false);
    }
  };

  // Load more transactions
  const loadMore = () => {
    if (hasMore && !loading) {
      fetchStatement(currentPage + 1, false);
    }
  };

  // Apply filters
  const applyFilters = () => {
    setCurrentPage(1);
    fetchStatement(1, true);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      type: 'all',
      date_from: '',
      date_to: '',
      search: ''
    });
    setCurrentPage(1);
    fetchStatement(1, true);
  };

  // Load data on mount and when filters change
  useEffect(() => {
    if (show) {
      fetchStatement(1, true);
    }
  }, [show]); // fetchStatement is stable, no need to include

  // Format date for input
  const formatDateForInput = (date) => {
    if (!date) return '';
    return new Date(date).toISOString().split('T')[0];
  };

  // Get status badge variant
  const getStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
        return 'danger';
      case 'pending':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  // Get type badge variant
  const getTypeBadge = (type) => {
    switch (type) {
      case 'credit':
        return 'success';
      case 'debit':
        return 'danger';
      case 'refund':
        return 'info';
      default:
        return 'primary';
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <FaHistory className="me-2" />
          Wallet Statement
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Current Balance */}
        <Card className="mb-4">
          <Card.Body className="text-center">
            <h4 className="text-primary mb-1">Current Balance</h4>
            <h2 className="fw-bold text-success">₹{currentBalance.toFixed(2)}</h2>
          </Card.Body>
        </Card>

        <Tabs activeKey={activeTab} onSelect={setActiveTab}>
          {/* Statement Tab */}
          <Tab eventKey="statement" title="Transaction History">
            {/* Filters */}
            <Card className="mb-3">
              <Card.Header>
                <FaFilter className="me-2" />
                Filters
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Transaction Type</Form.Label>
                      <Form.Select
                        value={filters.type}
                        onChange={(e) => setFilters({...filters, type: e.target.value})}
                      >
                        <option value="all">All Transactions</option>
                        <option value="credit">Money Added</option>
                        <option value="debit">Money Deducted</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>From Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={filters.date_from}
                        onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>To Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={filters.date_to}
                        onChange={(e) => setFilters({...filters, date_to: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3} className="d-flex align-items-end">
                    <div className="d-flex gap-2 w-100">
                      <Button variant="primary" onClick={applyFilters} size="sm">
                        <FaSearch className="me-1" />
                        Apply
                      </Button>
                      <Button variant="outline-secondary" onClick={resetFilters} size="sm">
                        Reset
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Transactions Table */}
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <span>Transaction History</span>
                  <small className="text-muted">
                    {transactions.length} transactions found
                  </small>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                {loading && transactions.length === 0 ? (
                  <div className="text-center p-4">
                    <Spinner animation="border" />
                    <p className="mt-2">Loading transactions...</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table hover className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Date & Time</th>
                          <th>Type</th>
                          <th>Description</th>
                          <th>Amount</th>
                          <th>Balance Before</th>
                          <th>Balance After</th>
                          <th>Status</th>
                          <th>Reference</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((transaction) => (
                          <tr key={transaction.id}>
                            <td>
                              <div>
                                <div className="fw-bold">{transaction.formatted_date}</div>
                                <small className="text-muted">ID: {transaction.transaction_id}</small>
                              </div>
                            </td>
                            <td>
                              <Badge bg={getTypeBadge(transaction.type)}>
                                {transaction.type_icon} {transaction.type_label}
                              </Badge>
                            </td>
                            <td>
                              <div>
                                <div>{transaction.note}</div>
                                {transaction.external_ref && (
                                  <small className="text-muted">
                                    Ref: {transaction.external_ref}
                                  </small>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className={`fw-bold ${
                                transaction.type === 'credit' || transaction.type === 'refund' 
                                  ? 'text-success' 
                                  : 'text-danger'
                              }`}>
                                {transaction.type === 'credit' || transaction.type === 'refund' ? '+' : '-'}
                                {transaction.formatted_amount}
                              </div>
                            </td>
                            <td>
                              <div className="fw-bold text-primary">
                                {transaction.formatted_balance_before || '₹0.00'}
                              </div>
                            </td>
                            <td>
                              <div className="fw-bold text-success">
                                {transaction.formatted_balance_after || '₹0.00'}
                              </div>
                            </td>
                            <td>
                              <Badge bg={getStatusBadge(transaction.status)}>
                                {transaction.status_icon} {transaction.status_label}
                              </Badge>
                            </td>
                            <td>
                              {transaction.external_ref ? (
                                <small className="text-muted">{transaction.external_ref}</small>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => {
                                  // View transaction details
                                  toast.info('Transaction details: ' + transaction.note);
                                }}
                              >
                                <FaEye />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* Load More Button */}
                {hasMore && (
                  <div className="text-center p-3 border-top">
                    <Button
                      variant="outline-primary"
                      onClick={loadMore}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Loading...
                        </>
                      ) : (
                        'Load More Transactions'
                      )}
                    </Button>
                  </div>
                )}

                {!loading && transactions.length === 0 && (
                  <div className="text-center p-4">
                    <FaHistory className="text-muted" size={48} />
                    <p className="mt-2 text-muted">No transactions found</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>

          {/* Summary Tab */}
          <Tab eventKey="summary" title="Summary">
            <Row>
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <FaMoneyBillWave className="me-2" />
                    Transaction Summary
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between">
                        <span>Total Transactions:</span>
                        <strong>{summary.total_transactions || 0}</strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>Successful:</span>
                        <span className="text-success">{summary.successful_transactions || 0}</span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>Failed:</span>
                        <span className="text-danger">{summary.failed_transactions || 0}</span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>Pending:</span>
                        <span className="text-warning">{summary.pending_transactions || 0}</span>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <FaArrowUp className="me-2 text-success" />
                    Money Added
                  </Card.Header>
                  <Card.Body>
                    <h4 className="text-success">{summary.formatted_successful_credit || '₹0.00'}</h4>
                    <small className="text-muted">Total Credit: {summary.formatted_total_credit || '₹0.00'}</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <FaArrowDown className="me-2 text-danger" />
                    Money Deducted
                  </Card.Header>
                  <Card.Body>
                    <h4 className="text-danger">{summary.formatted_successful_debit || '₹0.00'}</h4>
                    <small className="text-muted">Total Debit: {summary.formatted_total_debit || '₹0.00'}</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-primary" onClick={() => {
          // Export statement functionality
          toast.info('Export functionality coming soon!');
        }}>
          <FaDownload className="me-2" />
          Export Statement
        </Button>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WalletStatementModal;

